import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  Tooltip,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import moment from 'moment';
import ApprovedChip from '../../components/ApprovedChip';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import constants from '../../utilities/constants';
import Store from '../../utilities/store';
import OrderPartDialog from '../../components/OrderPartDialog';
import ServiceLineChip from '../../components/ServiceLineChip';
import { serviceLineConfirmText } from '../../utilities/partServiceLine';
import PartEditForm from '../../pages/PartsDashboardPage/PartEditForm';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../../utilities/ModalStackContext';
import { useTallDialogSizing } from '../../utilities/dialogSizing';

const PartsTable = ({ parts, title, theme, handlePartClick, handleStatusChange, getStatusColor, getStatusIcon, isAdmin, showSelection, isSelectable, selectedIds, onToggleSelect, onToggleSelectAll }) => {
  // A mechanic only gets checkboxes on the rows they may act on (pending parts),
  // so select-all and the header state are computed over selectable rows only.
  const selectable = parts.filter(isSelectable);
  const allSelected = selectable.length > 0 && selectable.every((p) => selectedIds.includes(p.id));
  const someSelected = selectable.some((p) => selectedIds.includes(p.id));
  return (
  <>
    <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
      {title} ({parts.length})
    </Typography>
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {showSelection && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  disabled={selectable.length === 0}
                  onChange={(e) => onToggleSelectAll(e.target.checked)}
                  title="Select all"
                />
              </TableCell>
            )}
            <TableCell>Part Name</TableCell>
            <TableCell>Vendor</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Order Date</TableCell>
            <TableCell>Arrival Date</TableCell>
            <TableCell align="right">Cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parts.map((part) => {
            // Order date, never `status_time`. status_time is rewritten on every
            // status change, so showing it here dated a part to the last time
            // anyone touched it — which reads as later than the customer's
            // ApprovedChip date. A part requested from the Chrome extension has
            // no orderDate until a parts admin actually orders it, so fall back
            // to `date` (the request date) and say so.
            const orderedOn = part.orderDate;
            const dateVal = orderedOn || part.date;
            const dateDisplay = dateVal
              ? moment(dateVal, ['YYYY/MM/DD', 'YYYY-MM-DD']).format('MMM DD, YYYY')
              : '-';
            return (
              <TableRow
                key={part.id}
                onClick={() => isAdmin && handlePartClick(part)}
                sx={{
                  cursor: isAdmin ? 'pointer' : 'default',
                  '&:hover': isAdmin ? {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  } : {}
                }}
              >
                {showSelection && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    {isSelectable(part) && (
                      <Checkbox
                        checked={selectedIds.includes(part.id)}
                        onChange={() => onToggleSelect(part.id)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, textDecoration: part.declined ? 'line-through' : 'none', color: part.declined ? 'text.disabled' : 'text.primary' }}
                    >
                      {part.name || 'Unnamed Part'}
                    </Typography>
                    {part.declined && <Chip label="Declined" color="error" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    <ApprovedChip line={part} />
                    <ServiceLineChip issue={part.serviceLineIssue} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {part.vendor || '-'}
                  </Typography>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Chip
                    icon={getStatusIcon(part.status)}
                    label={
                      part.status === 'complete'
                        ? 'Arrived'
                        : part.status
                        ? part.status.charAt(0).toUpperCase() + part.status.slice(1)
                        : 'Unknown'
                    }
                    onClick={() => handleStatusChange(part.id, part.status)}
                    disabled={!isAdmin}
                    sx={{
                      bgcolor: alpha(getStatusColor(part.status), 0.1),
                      color: getStatusColor(part.status),
                      border: `1px solid ${alpha(getStatusColor(part.status), 0.3)}`,
                      cursor: isAdmin ? 'pointer' : 'default',
                      '&:hover': isAdmin ? {
                        bgcolor: alpha(getStatusColor(part.status), 0.2),
                      } : {},
                      '& .MuiChip-icon': {
                        color: 'inherit'
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  {orderedOn ? (
                    <Typography variant="body2" color="text.secondary">
                      {dateDisplay}
                    </Typography>
                  ) : (
                    <Tooltip title="Requested on this date — not ordered yet">
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {dateDisplay}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {part.arrivalDate ? moment(part.arrivalDate, 'YYYY/MM/DD').format('MMM DD, YYYY') : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <AttachMoneyIcon sx={{ mr: 0.5, color: theme.palette.text.secondary, fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {part.cost ? `$${parseFloat(part.cost).toFixed(2)}` : '-'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </>
  );
};

const PartsBlade = ({ order, onClose, onUpdate }) => {
  const myZ = useStackZIndex();
  const { fullScreen, paperSx } = useTallDialogSizing({ height: '90vh', margin: 4 });
  const selectMenuProps = useSelectMenuProps(myZ);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [parts, setParts] = useState([]);
  // Service-line flag per part id ('none' | 'closed' | null). Kept apart from
  // `parts` because parts also arrive from the parent's listener via order.parts.
  const [lineIssues, setLineIssues] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pendingPartId, setPendingPartId] = useState(null);
  const [addPartDialogOpen, setAddPartDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  // isAdmin gates the parts *administration* controls: editing a part, changing
  // its status, and the bulk approve/decline/delete row. The parts-pipeline
  // permission grants it too, so a user whose role is not service/parts but who
  // runs the parts pipeline still gets the Order Parts button.
  const isAdmin = StateManager.isBackoffice() || StateManager.userType == "service" || StateManager.userType == "parts"
    || (StateManager.whiteList || []).includes('parts-pipeline');
  // Requesting a part is a separate, weaker permission. Mechanics need it — they
  // are the ones who find out a part is missing. OrderPartDialog already holds
  // them to the stricter non-backoffice validation (name + link or vendor/part
  // number + cost), and every request lands as 'pending' for a parts admin to
  // approve.
  const canRequestPart = isAdmin || StateManager.userType == "mechanic" || !!StateManager.mechanicId;
  // A mechanic may delete a part they requested by mistake, but only while it is
  // still 'pending' — once a parts admin approves it, money may be committed.
  const canDeletePart = (part) => isAdmin || (canRequestPart && part?.status === 'pending');
  const showSelection = isAdmin || canRequestPart;

  // Fetch parts for this order
  useEffect(() => {
    const fetchParts = async () => {
      try {
        setLoading(true);
        const snapshot = await firebase.firestore()
          .collection('parts')
          .where('order', '==', order.id)
          .get();

        if (snapshot.empty) {
          setParts([]);
          return;
        }

        const partsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setParts(partsData);
      } catch (error) {
        console.error('Error fetching parts:', error);
        StateManager.setAlertAndOpen('Error fetching parts', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchParts();
  }, [order.id]);

  // Keep parts state in sync with order prop
  useEffect(() => {
    if (order && order.parts) {
      setParts(order.parts);
    }
  }, [order?.parts]);

  const handleAddPart = () => {
    // The real-time listeners in the parent component will handle updates automatically
    // This function is called when a part is successfully added via OrderPartDialog
    console.log('Part added successfully');
  };

  const handlePartUpdate = async (partId, updates) => {
    try {
      const previousStatus = parts.find((p) => p.id === partId)?.status;
      const writeUpdates = { ...updates, updated_at: firebase.firestore.FieldValue.serverTimestamp() };
      if (updates.status && updates.status !== previousStatus) {
        writeUpdates.status_time = moment().format('YYYY/MM/DD');
      }
      await firebase.firestore().collection('parts').doc(partId).update(writeUpdates);

      // Update local state
      const updatedParts = parts.map(part =>
        part.id === partId ? { ...part, ...updates } : part
      );
      setParts(updatedParts);
      onUpdate && onUpdate(updatedParts);
    } catch (error) {
      console.error('Error updating part:', error);
      StateManager.setAlertAndOpen('Error updating part', 'error');
    }
  };

  const handleStatusChange = (partId, newStatus) => {
    if (isAdmin) {
      setPendingPartId(partId);
      setSelectedStatus(newStatus);
      setStatusDialogOpen(true);
    }
  };

  const handleStatusConfirm = () => {
    if (selectedStatus && pendingPartId) {
      const currentPart = parts.find(p => p.id === pendingPartId);

      // Block 'returned' if no return receipt uploaded
      if (selectedStatus === 'returned' && !currentPart?.returnReceipt) {
        StateManager.setAlertAndOpen("Upload a return receipt in the part edit form before marking as returned", "warning");
        return;
      }

      if (currentPart?.status === 'pending' && selectedStatus === 'approved') {
        const lineWarning = serviceLineConfirmText([{ ...currentPart, serviceLineIssue: lineIssues[currentPart.id] }]);
        if (lineWarning && !window.confirm(lineWarning)) return;
      }

      const updates = { status: selectedStatus };

      // When a part is marked 'complete' (on site), set arrival date if not already set
      if (selectedStatus === 'complete') {
        if (!currentPart?.arrivalDate) {
          updates.arrivalDate = moment().format('YYYY/MM/DD');
        }
      }

      handlePartUpdate(pendingPartId, updates);

      // Reset dialog state
      setStatusDialogOpen(false);
      setSelectedStatus('');
      setPendingPartId(null);
    }
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen(false);
    setSelectedStatus('');
    setPendingPartId(null);
  };

  const handleToggleSelect = (partId) => {
    setSelectedIds(prev =>
      prev.includes(partId) ? prev.filter(id => id !== partId) : [...prev, partId]
    );
  };

  const handleToggleSelectAll = (checked) => {
    setSelectedIds(checked ? filteredParts.filter(canDeletePart).map(p => p.id) : []);
  };

  const handleBulkDelete = async (partIds) => {
    // Re-check the rule at submit time: a part can be approved out from under a
    // mechanic between selecting it and pressing Delete.
    const ids = (partIds || []).filter(Boolean).filter(id => canDeletePart(parts.find(p => p.id === id)));
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} part${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      const batch = firebase.firestore().batch();
      // Deleting a part also removes its linked purchase via the
      // syncPartPurchase Cloud Function trigger.
      ids.forEach((id) => batch.delete(firebase.firestore().collection('parts').doc(id)));
      await batch.commit();

      const updatedParts = parts.filter(part => !ids.includes(part.id));
      setParts(updatedParts);
      onUpdate && onUpdate(updatedParts);
      setSelectedIds([]);
      StateManager.setAlertAndOpen(`Deleted ${ids.length} part${ids.length > 1 ? 's' : ''}`, 'success');
    } catch (error) {
      console.error('Error deleting parts:', error);
      StateManager.setAlertAndOpen('Error deleting parts', 'error');
    }
  };

  const handleBulkDecline = async (partIds) => {
    const ids = (partIds || []).filter(Boolean);
    if (ids.length === 0) return;
    if (!window.confirm(`Decline ${ids.length} part${ids.length > 1 ? 's' : ''}? They'll be excluded from accounting.`)) return;
    try {
      const now = moment().format('YYYY/MM/DD');
      const batch = firebase.firestore().batch();
      // Mirror the single-part decline: terminal 'declined' status + boolean,
      // remembering the prior status so Reinstate can restore it.
      ids.forEach((id) => {
        const part = parts.find(p => p.id === id);
        batch.update(firebase.firestore().collection('parts').doc(id), {
          declined: true,
          status: 'declined',
          restoreStatus: part?.status || 'pending',
          status_time: now,
          updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        });
      });
      await batch.commit();

      const updatedParts = parts.map(part =>
        ids.includes(part.id)
          ? { ...part, declined: true, status: 'declined', restoreStatus: part.status || 'pending', status_time: now }
          : part
      );
      setParts(updatedParts);
      onUpdate && onUpdate(updatedParts);
      setSelectedIds([]);
      StateManager.setAlertAndOpen(`Declined ${ids.length} part${ids.length > 1 ? 's' : ''} — excluded from accounting`, 'warning');
    } catch (error) {
      console.error('Error declining parts:', error);
      StateManager.setAlertAndOpen('Error declining parts', 'error');
    }
  };

  const handleBulkApprove = async (partIds) => {
    const ids = (partIds || []).filter(Boolean);
    if (ids.length === 0) return;
    // Parts not tied to an open service line may be for work that won't happen.
    const lineWarning = serviceLineConfirmText(
      parts.filter(part => ids.includes(part.id)).map(part => ({ ...part, serviceLineIssue: lineIssues[part.id] }))
    );
    if (lineWarning && !window.confirm(lineWarning)) return;
    try {
      const now = moment().format('YYYY/MM/DD');
      const batch = firebase.firestore().batch();
      ids.forEach((id) => {
        const ref = firebase.firestore().collection('parts').doc(id);
        batch.update(ref, {
          status: 'approved',
          status_time: now,
          updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        });
      });
      await batch.commit();

      const updatedParts = parts.map(part =>
        ids.includes(part.id) ? { ...part, status: 'approved', status_time: now } : part
      );
      setParts(updatedParts);
      onUpdate && onUpdate(updatedParts);
      setSelectedIds([]);
      StateManager.setAlertAndOpen(`Approved ${ids.length} part${ids.length > 1 ? 's' : ''}`, 'success');
    } catch (error) {
      console.error('Error approving parts:', error);
      StateManager.setAlertAndOpen('Error approving parts', 'error');
    }
  };

  const carTitle = order.car?.title
    || `${order.car?.stock || ""} - ${order.car?.year || ""} ${order.car?.make || ""} ${order.car?.model || ""}`.trim()
    || '';

  const handlePartClick = (part) => {
    // Enrich part with car data if available
    const enrichedPart = {
      ...part,
      car_thumbnail: order.car?.thumbnail || order.thumbnail || null,
      car_title: carTitle
    };
    setSelectedPart(enrichedPart);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedPart(null);
  };

  const handlePartEditUpdate = async (updates) => {
    if (!selectedPart) return;

    try {
      const previousStatus = selectedPart.status;
      const writeUpdates = { ...updates, updated_at: firebase.firestore.FieldValue.serverTimestamp() };
      if (updates.status && updates.status !== previousStatus) {
        writeUpdates.status_time = moment().format('YYYY/MM/DD');
      }
      await firebase.firestore().collection('parts').doc(selectedPart.id).update(writeUpdates);

      // Update local state
      const updatedParts = parts.map(part =>
        part.id === selectedPart.id ? { ...part, ...updates } : part
      );
      setParts(updatedParts);
      setSelectedPart({ ...selectedPart, ...updates });
      onUpdate && onUpdate(updatedParts);
    } catch (error) {
      console.error('Error updating part:', error);
      StateManager.setAlertAndOpen('Error updating part', 'error');
    }
  };

  // Undo of "Add to SO" on the Parts Inventory page: puts the stock back on
  // the shelf and removes the part from this SO.
  const handleReturnToInventory = async () => {
    if (!selectedPart) return;
    if (!window.confirm('Return this part to inventory and remove it from this SO?')) return;

    try {
      await Store.returnPartToInventory(selectedPart.id);
      const updatedParts = parts.filter(p => p.id !== selectedPart.id);
      setParts(updatedParts);
      onUpdate && onUpdate(updatedParts);
      handleEditDialogClose();
      StateManager.setAlertAndOpen('Part returned to inventory', 'success');
    } catch (error) {
      console.error('Error returning part to inventory:', error);
      StateManager.setAlertAndOpen(error?.message || 'Error returning part to inventory', 'error');
    }
  };

  const handlePartEditDelete = async () => {
    if (!selectedPart) return;

    if (!window.confirm("Are you sure you want to delete this part?")) {
      return;
    }

    try {
      await firebase.firestore().collection('parts').doc(selectedPart.id).delete();

      // Remove from local state
      const updatedParts = parts.filter(p => p.id !== selectedPart.id);
      setParts(updatedParts);
      onUpdate && onUpdate(updatedParts);

      // Close the dialog
      handleEditDialogClose();

      StateManager.setAlertAndOpen('Part deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting part:', error);
      StateManager.setAlertAndOpen('Error deleting part', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return theme.palette.info.light;
      case 'approved':
        return theme.palette.info.main;
      case 'inbound':
        return theme.palette.primary.main;
      case 'returning':
        return theme.palette.error.main;
      case 'returned':
        return theme.palette.success.dark;
      case 'complete':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <InventoryIcon />;
      case 'approved':
        return <CheckCircleIcon />;
      case 'inbound':
        return <LocalShippingIcon />;
      case 'returning':
        return <WarningIcon />;
      case 'returned':
        return <WarningIcon />;
      case 'complete':
        return <CheckCircleIcon />;
      default:
        return <PendingIcon />;
    }
  };

  // Recompute the flags when a part's link or status changes.
  const lineIssueKey = parts.map(p => `${p.id}:${p.serviceId ?? '-'}:${p.status}`).join('|');
  useEffect(() => {
    let active = true;
    Store.attachServiceLineIssues(parts)
      .then(list => { if (active) setLineIssues(Object.fromEntries(list.map(p => [p.id, p.serviceLineIssue]))); })
      .catch(err => console.error('Error loading service lines for parts:', err));
    return () => { active = false; };
  }, [lineIssueKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredParts = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    const filtered = parts.map(part => ({ ...part, serviceLineIssue: lineIssues[part.id] || null })).filter(part =>
      part.name?.toLowerCase().includes(searchLower) ||
      part.vendor?.toLowerCase().includes(searchLower) ||
      part.status?.toLowerCase().includes(searchLower)
    );
    return filtered.sort((a, b) => {
      // Declined parts are grouped at the bottom; everything else by date desc.
      if (!!a.declined !== !!b.declined) return a.declined ? 1 : -1;
      const dateA = a.orderDate || a.date;
      const dateB = b.orderDate || b.date;
      const da = dateA ? moment(dateA, ['YYYY/MM/DD', 'YYYY-MM-DD']) : moment(0);
      const db = dateB ? moment(dateB, ['YYYY/MM/DD', 'YYYY-MM-DD']) : moment(0);
      return db.valueOf() - da.valueOf();
    });
  }, [parts, searchQuery, lineIssues]);

  // Calculate total cost — declined parts are excluded from accounting.
  const totalCost = parts.reduce((sum, part) => sum + (part.declined ? 0 : (parseFloat(part.cost) || 0)), 0);

  const pendingParts = parts.filter(p => p.status === 'pending');
  // Approve only acts on pending parts, even if completed parts are also selected.
  const selectedPendingIds = selectedIds.filter(id => parts.find(p => p.id === id)?.status === 'pending');
  // Decline only acts on parts that aren't already declined.
  const selectedActiveIds = selectedIds.filter(id => !parts.find(p => p.id === id)?.declined);
  // Delete acts on whatever the user is allowed to delete — everything for an
  // admin, pending parts only for a mechanic.
  const selectedDeletableIds = selectedIds.filter(id => canDeletePart(parts.find(p => p.id === id)));

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Typography>Loading parts...</Typography>
      </Box>
    );
  }

  return (
    <ModalStackProvider value={myZ}>
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search parts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {canRequestPart && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddPartDialogOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {isAdmin ? 'Order Parts' : 'Request Parts'}
          </Button>
        )}
      </Box>

      {/* Total Cost Summary */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mb: 2,
          px: 2,
          py: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Total Cost:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Box>

      {/* Bulk actions */}
      {showSelection && parts.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled={selectedPendingIds.length === 0}
                onClick={() => handleBulkApprove(selectedPendingIds)}
              >
                Approve Selected ({selectedPendingIds.length})
              </Button>
              {pendingParts.length > 0 && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleBulkApprove(pendingParts.map(p => p.id))}
                >
                  Approve All Pending ({pendingParts.length})
                </Button>
              )}
              <Button
                variant="contained"
                color="warning"
                startIcon={<BlockIcon />}
                disabled={selectedActiveIds.length === 0}
                onClick={() => handleBulkDecline(selectedActiveIds)}
              >
                Decline Selected ({selectedActiveIds.length})
              </Button>
            </>
          )}
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={selectedDeletableIds.length === 0}
            onClick={() => handleBulkDelete(selectedDeletableIds)}
          >
            Delete Selected ({selectedDeletableIds.length})
          </Button>
        </Box>
      )}

      {filteredParts.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{ py: 4 }}>
          <WarningIcon sx={{ fontSize: 48, color: theme.palette.warning.main }} />
          <Typography variant="h6" color="text.secondary">
            No parts found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No parts have been added to this service order yet.
          </Typography>
          {canRequestPart && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddPartDialogOpen(true)}
            >
              {isAdmin ? 'Order Parts' : 'Request Parts'}
            </Button>
          )}
        </Box>
      ) : (
        <PartsTable
          parts={filteredParts}
          title="All Parts"
          theme={theme}
          handlePartClick={handlePartClick}
          handleStatusChange={handleStatusChange}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          isAdmin={isAdmin}
          showSelection={showSelection}
          isSelectable={canDeletePart}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
        />
      )}

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onClose={handleStatusCancel} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
        <DialogTitle>Update Part Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select the new status for this part:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status"
              MenuProps={selectMenuProps}
            >
              {constants.part_statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusCancel}>Cancel</Button>
          <Button
            onClick={handleStatusConfirm}
            variant="contained"
            disabled={!selectedStatus}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Part Dialog */}
      <OrderPartDialog
        open={addPartDialogOpen}
        onClose={() => setAddPartDialogOpen(false)}
        order={{ ...order, car_title: order.car_title || carTitle }}
        onSuccess={handleAddPart}
      />

      {/* Part Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        sx={{ zIndex: myZ }}
        PaperProps={{ sx: paperSx }}
      >
        <DialogContent sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.grey[100],
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.grey[400],
            borderRadius: '4px',
          },
        }}>
          {selectedPart?.fromInventory && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" startIcon={<InventoryIcon />} onClick={handleReturnToInventory}>
                  Return to Inventory
                </Button>
              }
            >
              This part came from Parts Inventory.
            </Alert>
          )}
          {selectedPart && (
            <PartEditForm
              part={selectedPart}
              onUpdate={handlePartEditUpdate}
              onDelete={handlePartEditDelete}
              onCancel={handleEditDialogClose}
              onSubmit={handleEditDialogClose}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
    </ModalStackProvider>
  );
};

export default PartsBlade;
